import { useTranslation } from 'react-i18next'

import {
  useMassActions,
  useNotification,
  usePerformMassAction,
} from '../../../../../hooks'
import { ConfirmationModal } from '../../../../Confirmation'
import { CONFIRMATION_QA_NAME_FORM_MODAL } from '../../../../Form'
import { performContractsMassAction } from '../../EntityTabContracts.api'
import {
  CONTRACTS_MASS_ACTION_NAMES,
  ENTITY_TAB_CONTRACTS_ITEM_ACTION,
} from '../../EntityTabContracts.constants'
import {
  getContractsMassActionNotificationTitle,
  isContractsArchiveAction,
} from '../../EntityTabContracts.utils'

export function useEntityTabContractsMassActions({
  archiveAllApi,
  excludeIds,
  filterValues,
  pagination,
  scopeFilterKey,
  scopeId,
  selectAllPages,
  selectedIds,
  sub,
  onCleanSelection,
  onCloseModal,
  onEntityReload,
  onReload,
  onSetModal,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  function handleReload() {
    onReload?.()
    onEntityReload?.()
  }

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getNotificationTitle: getContractsMassActionNotificationTitle,
    onSuccessHandler: handleReload,
  })

  async function performApi(params) {
    return performContractsMassAction({
      ...params,
      filters: {
        ...params.filters,
        [scopeFilterKey]: [scopeId],
        sub_menu: sub,
      },
    })
  }

  function handleErrorAction() {
    handleAddErrorNotification('Notification.ContractsArchiveErrorTitleKey')
  }

  const { isPerformingMassAction, massAction, setPerformingMassAction } =
    usePerformMassAction({
      excludeIds,
      filterValues,
      getAsyncTaskId,
      isUuid: false,
      massActionNames: CONTRACTS_MASS_ACTION_NAMES,
      pagination,
      performApi,
      selectAllPages,
      selectedIds,
      sub,
      onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
      onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
      onCleanSelection,
      onCloseModal,
      onErrorAction: handleErrorAction,
      onReload: handleReload,
    })

  function getIsArchivingAllContracts() {
    if (!archiveAllApi) {
      return false
    }

    if (selectAllPages) {
      return excludeIds.length === 0
    }

    return selectedIds.length > 0 && selectedIds.length === pagination?.count
  }

  function archiveAllContracts() {
    onCloseModal()

    setPerformingMassAction(true)

    archiveAllApi({ scopeId })
      .then(({ data }) => {
        onCleanSelection()

        handleReload()

        handleAddFinishMassActionNotification(
          { items: data },
          {
            action: ENTITY_TAB_CONTRACTS_ITEM_ACTION.Archive,
            totalCount: pagination?.count,
          }
        )
      })
      .catch(handleErrorAction)
      .finally(() => {
        setPerformingMassAction(false)
      })
  }

  function showArchiveConfirm(onSubmit, isArchivingAllContracts) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          cancelText: t('Modal.CancelBtnKey'),
          submitText: t('Modal.ContractArchiveBtnKey'),
        }}
        description={t(
          isArchivingAllContracts
            ? 'Modal.ContractArchiveAllConfirmationTextKey'
            : 'Modal.ContractArchiveConfirmationTextKey'
        )}
        qaName={CONFIRMATION_QA_NAME_FORM_MODAL}
        onCancel={onCloseModal}
        onSubmit={onSubmit}
      />
    )
  }

  function handleArchiveItem(item) {
    const isArchivingAllContracts = selectAllPages
      ? getIsArchivingAllContracts()
      : Boolean(archiveAllApi) && pagination?.count === 1

    showArchiveConfirm(
      () =>
        isArchivingAllContracts
          ? archiveAllContracts()
          : massAction({
              action: ENTITY_TAB_CONTRACTS_ITEM_ACTION.Archive,
              ids: item.id,
            }),
      isArchivingAllContracts
    )
  }

  function setMassAction(action) {
    if (isContractsArchiveAction(action)) {
      const isArchivingAllContracts = getIsArchivingAllContracts()

      showArchiveConfirm(
        () =>
          isArchivingAllContracts
            ? archiveAllContracts()
            : massAction({ action }),
        isArchivingAllContracts
      )
    }
  }

  return { handleArchiveItem, isPerformingMassAction, setMassAction }
}
